import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Building2, Mail, Lock, User } from 'lucide-react';
import { auth } from '../api';
import { sessionManager } from '../utils/auth';
import { useNavigate } from 'react-router-dom';

const Login = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('google');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [signupData, setSignupData] = useState({ name: '', email: '', password: '', confirmPassword: '' });

  const handleGoogleLogin = () => {
    sessionStorage.setItem('auth_redirect', window.location.origin + '/dashboard');
    const redirectUrl = window.location.origin + '/auth/callback';
    const authUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    window.location.replace(authUrl);
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await auth.login(loginData);
      
      // Store session token - check multiple sources for mobile compatibility
      // Headers are lowercased by axios, so check lowercase version
      const sessionToken = response.data.session_token || 
                          response.data.sessionToken ||
                          response.headers['x-session-token'];
      
      console.log('📱 Login response received, token:', sessionToken ? 'found' : 'not found');
      
      if (sessionToken) {
        sessionManager.setToken(sessionToken);
        
        // Verify storage worked
        const stored = sessionManager.getToken();
        console.log('📱 Token storage verified:', stored ? 'success' : 'failed');
        
        if (!stored) {
          // Fallback: try again with a delay
          await new Promise(resolve => setTimeout(resolve, 100));
          sessionManager.setToken(sessionToken);
        }
      } else {
        console.error('❌ No session token received from server');
        setError('Login failed: No session token received');
        setLoading(false);
        return;
      }
      
      // Longer delay for mobile browsers to ensure storage persistence
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Navigate to dashboard using navigate for better SPA handling
      navigate('/dashboard');
    } catch (err) {
      console.error('❌ Login error:', err);
      setError(err.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');

    // Validate passwords match
    if (signupData.password !== signupData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password length
    if (signupData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const response = await auth.signup({
        name: signupData.name,
        email: signupData.email,
        password: signupData.password
      });
      
      // Store session token - check multiple sources for mobile compatibility
      const sessionToken = response.data.session_token || 
                          response.data.sessionToken ||
                          response.headers['x-session-token'];
      
      console.log('📱 Signup response received, token:', sessionToken ? 'found' : 'not found');
      
      if (sessionToken) {
        sessionManager.setToken(sessionToken);
        
        // Verify storage worked
        const stored = sessionManager.getToken();
        console.log('📱 Token storage verified:', stored ? 'success' : 'failed');
        
        if (!stored) {
          await new Promise(resolve => setTimeout(resolve, 100));
          sessionManager.setToken(sessionToken);
        }
      } else {
        console.error('❌ No session token received from server');
        setError('Signup failed: No session token received');
        setLoading(false);
        return;
      }
      
      // Longer delay for mobile browsers
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Navigate to dashboard
      navigate('/dashboard');
    } catch (err) {
      console.error('❌ Signup error:', err);
      setError(err.response?.data?.detail || 'Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-red-50 p-4">
      <div className="w-full max-w-md p-8 bg-white rounded-2xl shadow-2xl border border-gray-100">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <div className="flex items-center gap-2">
              <Building2 className="w-10 h-10 text-blue-600" />
              <h1 className="text-4xl font-bold">
                <span className="text-red-600">Turbo</span>
                <span className="text-blue-600">Books</span>
              </h1>
            </div>
          </div>
          <p className="text-gray-600 text-lg">Smart Accounting for Modern Businesses</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="google">Google</TabsTrigger>
            <TabsTrigger value="login">Login</TabsTrigger>
            <TabsTrigger value="signup">Sign Up</TabsTrigger>
          </TabsList>

          {/* Google Login Tab */}
          <TabsContent value="google">
            <div className="space-y-4">
              <div className="text-center mb-4">
                <h2 className="text-xl font-semibold text-gray-800 mb-2">Sign in with Google</h2>
                <p className="text-sm text-gray-600">Quick and secure access</p>
              </div>

              <Button
                onClick={handleGoogleLogin}
                className="w-full h-12 text-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
              >
                <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </Button>
            </div>
          </TabsContent>

          {/* Email Login Tab */}
          <TabsContent value="login">
            <form onSubmit={handleEmailLogin} className="space-y-4">
              <div className="text-center mb-4">
                <h2 className="text-xl font-semibold text-gray-800 mb-2">Login with Email</h2>
                <p className="text-sm text-gray-600">Access your account</p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div>
                <Label htmlFor="login-email" className="flex items-center gap-2 mb-2">
                  <Mail className="h-4 w-4" />
                  Email Address
                </Label>
                <Input
                  id="login-email"
                  type="email"
                  value={loginData.email}
                  onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                  required
                  placeholder="your@email.com"
                  className="h-11"
                />
              </div>

              <div>
                <Label htmlFor="login-password" className="flex items-center gap-2 mb-2">
                  <Lock className="h-4 w-4" />
                  Password
                </Label>
                <Input
                  id="login-password"
                  type="password"
                  value={loginData.password}
                  onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                  required
                  placeholder="Enter your password"
                  className="h-11"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-semibold"
              >
                {loading ? 'Logging in...' : 'Login'}
              </Button>
            </form>
          </TabsContent>

          {/* Sign Up Tab */}
          <TabsContent value="signup">
            <form onSubmit={handleSignup} className="space-y-4">
              <div className="text-center mb-4">
                <h2 className="text-xl font-semibold text-gray-800 mb-2">Create Account</h2>
                <p className="text-sm text-gray-600">Join TurboBooks today</p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div>
                <Label htmlFor="signup-name" className="flex items-center gap-2 mb-2">
                  <User className="h-4 w-4" />
                  Full Name
                </Label>
                <Input
                  id="signup-name"
                  type="text"
                  value={signupData.name}
                  onChange={(e) => setSignupData({...signupData, name: e.target.value})}
                  required
                  placeholder="John Doe"
                  className="h-11"
                />
              </div>

              <div>
                <Label htmlFor="signup-email" className="flex items-center gap-2 mb-2">
                  <Mail className="h-4 w-4" />
                  Email Address
                </Label>
                <Input
                  id="signup-email"
                  type="email"
                  value={signupData.email}
                  onChange={(e) => setSignupData({...signupData, email: e.target.value})}
                  required
                  placeholder="your@email.com"
                  className="h-11"
                />
              </div>

              <div>
                <Label htmlFor="signup-password" className="flex items-center gap-2 mb-2">
                  <Lock className="h-4 w-4" />
                  Password
                </Label>
                <Input
                  id="signup-password"
                  type="password"
                  value={signupData.password}
                  onChange={(e) => setSignupData({...signupData, password: e.target.value})}
                  required
                  placeholder="At least 6 characters"
                  className="h-11"
                />
              </div>

              <div>
                <Label htmlFor="signup-confirm" className="flex items-center gap-2 mb-2">
                  <Lock className="h-4 w-4" />
                  Confirm Password
                </Label>
                <Input
                  id="signup-confirm"
                  type="password"
                  value={signupData.confirmPassword}
                  onChange={(e) => setSignupData({...signupData, confirmPassword: e.target.value})}
                  required
                  placeholder="Re-enter password"
                  className="h-11"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-semibold"
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </Button>
            </form>
          </TabsContent>
        </Tabs>

        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="flex justify-center gap-8 text-sm text-gray-600">
            <div className="text-center">
              <div className="font-semibold text-blue-600">✓</div>
              <div>Multi-Business</div>
            </div>
            <div className="text-center">
              <div className="font-semibold text-red-600">✓</div>
              <div>Invoicing</div>
            </div>
            <div className="text-center">
              <div className="font-semibold text-blue-600">✓</div>
              <div>Reports</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
